import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ChevronLeft, ChevronRight, Search } from 'lucide-react';

import { EmptyState, Panel, Spinner, relativeTime } from '../../components/common/index.jsx';
import { admin } from '../../lib/api.js';
import { StatusChip } from '../QueuePage.jsx';

/** The admin's cross-annotator view of every caption in the corpus. */
const PAGE_SIZE = 200;

export function SamplesPage() {
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState({ scope: 0, flagged: 0, attention: 0 });
  const [totalIsExact, setTotalIsExact] = useState(true);
  const [offset, setOffset] = useState(0);
  const [datasets, setDatasets] = useState([]);
  const [conversions, setConversions] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const [filters, setFilters] = useState({
    dataset: '',
    family: '',
    status: '',
    needs_retag: false,
    flagged: false,
    q: '',
  });

  useEffect(() => {
    admin.datasets().then((r) => setDatasets(r.items)).catch(() => {});
    admin.legacyConversions().then((r) => setConversions(r.items)).catch(() => {});
    admin.ingestConflicts().then((r) => setConflicts(r.items)).catch(() => {});
  }, []);

  useEffect(() => {
    const handle = setTimeout(() => {
      admin
        .samples({
          dataset: filters.dataset || undefined,
          family: filters.family || undefined,
          status: filters.status || undefined,
          needs_retag: filters.needs_retag || undefined,
          flagged: filters.flagged || undefined,
          q: filters.q || undefined,
          limit: PAGE_SIZE,
          offset,
        })
        .then((r) => {
          setRows(r.items);
          // A server older than this page sends no total. Fall back to the row count,
          // but remember that it is only a lower bound - otherwise a full page reads as
          // "200 captions" and looks like the whole corpus.
          setTotalIsExact(r.total !== undefined);
          setTotal(r.total ?? r.items.length);
          setSummary({
            scope: r.scope_total ?? r.total ?? r.items.length,
            flagged: r.flagged_total ?? 0,
            attention: r.needs_attention_total ?? 0,
          });
        })
        .catch(() => {
          setRows([]);
          setTotal(0);
          setSummary({ scope: 0, flagged: 0, attention: 0 });
        });
    }, 220);
    return () => clearTimeout(handle);
  }, [filters, offset]);

  // Any filter change re-queries from the top; staying on page 3 of a narrower result
  // set would show an empty table.
  const set = (patch) => {
    setOffset(0);
    setFilters((f) => ({ ...f, ...patch }));
  };

  const pageStart = total === 0 ? 0 : offset + 1;
  const pageEnd = offset + (rows?.length ?? 0);
  const hasMore = offset + PAGE_SIZE < total;

  if (rows === null) return <Spinner label="Loading captions…" />;

  return (
    <div className="page wide">
      <div className="page-head">
        <h1>All captions</h1>
        <p>Every caption in the corpus, with whoever annotated it.</p>
      </div>

      {conflicts.length > 0 && (
        <div className="banner warn section">
          <AlertTriangle size={16} />
          <span className="grow">
            <strong>{conflicts.length} pipeline change{conflicts.length === 1 ? '' : 's'} were refused</strong>
            {' '}because they would have invalidated existing annotations. The stored data is
            unchanged; the affected annotations are flagged below.
          </span>
        </div>
      )}

      {conversions.length > 0 && (
        <div className="banner info section">
          <span className="grow">
            {conversions.length} legacy span{conversions.length === 1 ? '' : 's'} using the retired{' '}
            <code>unverifiable</code> label {conversions.length === 1 ? 'was' : 'were'} imported as{' '}
            <strong>hallucination</strong>, keeping the original note. The untouched original is
            preserved in the import snapshot.
          </span>
        </div>
      )}

      <Panel
        title={
          !totalIsExact && rows.length === PAGE_SIZE
            ? `First ${rows.length} captions`
            : total > rows.length
              ? `${pageStart}–${pageEnd} of ${total} captions`
              : `${total} caption${total === 1 ? '' : 's'}`
        }
        actions={
          <div className="row gap-1 wrap">
            <select className="select sm" style={{ width: 'auto' }} value={filters.dataset} onChange={(e) => set({ dataset: e.target.value })}>
              <option value="">All datasets</option>
              {datasets.map((d) => (
                <option key={d.key} value={d.key}>{d.title}</option>
              ))}
            </select>
            <select className="select sm" style={{ width: 'auto' }} value={filters.status} onChange={(e) => set({ status: e.target.value })}>
              <option value="">Any status</option>
              <option value="in_progress">In progress</option>
              <option value="submitted">Submitted</option>
              <option value="approved">Approved</option>
              <option value="changes_requested">Changes requested</option>
            </select>
            <span className="row gap-1">
              <Search size={14} className="faint" />
              <input
                className="input sm"
                placeholder="Search captions…"
                value={filters.q}
                onChange={(e) => set({ q: e.target.value })}
                aria-label="Search caption text"
              />
            </span>
          </div>
        }
        bodyClass="tight"
      >
        <div className="row gap-1 wrap count-chips">
          <button
            type="button"
            className={`chip toggle ${!filters.flagged && !filters.needs_retag ? 'active' : ''}`}
            onClick={() => set({ flagged: false, needs_retag: false })}
          >
            {summary.scope} total
          </button>
          <button
            type="button"
            className={`chip toggle flagged ${filters.flagged ? 'active' : ''}`}
            aria-pressed={filters.flagged}
            onClick={() => set({ flagged: !filters.flagged, needs_retag: false })}
            title="Captions the pipeline flagged as needing a human decision"
          >
            {summary.flagged} need human review
          </button>
          {summary.attention > 0 && (
            <button
              type="button"
              className={`chip toggle warn ${filters.needs_retag ? 'active' : ''}`}
              aria-pressed={filters.needs_retag}
              onClick={() => set({ needs_retag: !filters.needs_retag, flagged: false })}
              title="Annotations flagged after a pipeline change, or with an unresolved correction"
            >
              {summary.attention} need attention
            </button>
          )}
        </div>

        {rows.length === 0 ? (
          <EmptyState title="Nothing matches">Try relaxing the filters.</EmptyState>
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Image</th>
                  <th>Model</th>
                  <th>Annotator</th>
                  <th>Status</th>
                  <th className="num">H</th>
                  <th className="num">U</th>
                  <th className="num">W</th>
                  <th className="num">Decisions</th>
                  <th>Updated</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={`${row.sample_id}-${row.annotation_id ?? 'none'}`}>
                    <td>
                      <Link to={`/annotate/${row.task_id}${row.owner_user_id ? `?owner=${row.owner_user_id}` : ''}`}>
                        {row.image_id}
                      </Link>
                      <div className="tiny faint">
                        {row.family === 'coco' ? 'COCO' : 'Web'}
                        {row.human_review_flag && ' · agent flagged'}
                        {row.needs_retag && ' · needs attention'}
                      </div>
                    </td>
                    <td className="tiny">{row.caption_source}</td>
                    <td>
                      {row.owner_display_name || <span className="faint">unassigned</span>}
                      {row.edited_by_someone_else && (
                        <div className="tiny faint">
                          edited by {row.last_edited_by_display_name}
                        </div>
                      )}
                    </td>
                    <td>{row.status ? <StatusChip status={row.status} /> : <span className="chip">no work</span>}</td>
                    <td className="num">{row.n_hallucinated ?? 0}</td>
                    <td className="num">{row.n_uncertain ?? 0}</td>
                    <td className="num">{row.n_wrong_uncertainty ?? 0}</td>
                    <td className="num">
                      {row.n_decisions_made ?? 0}/{row.final_span_count}
                    </td>
                    <td className="tiny faint nowrap">{relativeTime(row.updated_at)}</td>
                    <td className="right">
                      <Link
                        className="btn sm"
                        to={`/annotate/${row.task_id}${row.owner_user_id ? `?owner=${row.owner_user_id}` : ''}`}
                      >
                        Review
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {total > PAGE_SIZE && (
          <div className="row gap-1 pager">
            <button
              className="btn sm"
              disabled={offset === 0}
              onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
            >
              <ChevronLeft size={14} /> Previous
            </button>
            <span className="tiny muted grow center">
              {pageStart}–{pageEnd} of {total}
            </span>
            <button className="btn sm" disabled={!hasMore} onClick={() => setOffset((o) => o + PAGE_SIZE)}>
              Next <ChevronRight size={14} />
            </button>
          </div>
        )}
      </Panel>
    </div>
  );
}
